import { useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { 
  dashboardApi, 
  usersApi, 
  ridesApi, 
  financeApi,
  rolesApi,
  permissionsApi,
  claimsApi
} from '../services/api'
import { mapFinanceOverview } from '../lib/adminMappers'
import { queryKeys, staleTimes } from './useQueries'

/**
 * Hook pour précharger les données critiques APRÈS le rendu initial
 * Utilise un délai pour ne pas bloquer l'UI au démarrage
 *
 * IMPORTANT : chaque prefetch est filtré par les permissions réellement
 * accordées par le backend. On ne lance JAMAIS un appel que l'utilisateur
 * n'a pas le droit de consommer (sinon : 403 inutiles à chaque démarrage).
 */
export function usePrefetchCriticalData() {
  const queryClient = useQueryClient()
  const hasPrefetched = useRef(false)

  useEffect(() => {
    // Ne précharger qu'une seule fois
    if (hasPrefetched.current) return
    hasPrefetched.current = true

    // Permissions persistées dans le store (disponibles immédiatement).
    const { hasPermission } = useAuthStore.getState()

    // Phase 1: Dashboard (priorité haute) - après 100ms pour laisser l'UI se rendre
    const timer1 = setTimeout(async () => {
      if (!hasPermission('dashboard.view')) return
      try {
        await Promise.allSettled([
          queryClient.prefetchQuery({
            queryKey: queryKeys.dashboardStats,
            queryFn: async () => {
              const { data } = await dashboardApi.getStats()
              return data.data
            },
            staleTime: staleTimes.frequent,
          }),
          queryClient.prefetchQuery({
            queryKey: queryKeys.dashboardRides({ limit: 10 }),
            queryFn: async () => {
              const { data } = await dashboardApi.getRides({ limit: 10 })
              return data.data
            },
            staleTime: staleTimes.realtime,
          }),
        ])
      } catch {
        // Silently fail
      }
    }, 100)

    // Phase 2: Autres données (priorité basse) - après 1 seconde
    const timer2 = setTimeout(async () => {
      try {
        const prefetches: Promise<unknown>[] = []

        if (hasPermission('users.view')) {
          prefetches.push(
            queryClient.prefetchQuery({
              queryKey: queryKeys.users({ page: 1 }),
              queryFn: async () => {
                const { data } = await usersApi.list({ page: 1 })
                return data.data
              },
              staleTime: staleTimes.standard,
            }),
          )
        }

        if (hasPermission('rides.view')) {
          prefetches.push(
            queryClient.prefetchQuery({
              queryKey: queryKeys.rides({ page: 1 }),
              queryFn: async () => {
                const { data } = await ridesApi.list({ page: 1 })
                return data.data
              },
              staleTime: staleTimes.realtime,
            }),
          )
        }

        if (hasPermission('finance.view')) {
          prefetches.push(
            queryClient.prefetchQuery({
              queryKey: queryKeys.financeOverview,
              queryFn: async () => {
                const { data } = await financeApi.overview()
                return mapFinanceOverview(data?.data ?? data)
              },
              staleTime: staleTimes.frequent,
            }),
          )
        }

        await Promise.allSettled(prefetches)
      } catch {
        // Silently fail
      }
    }, 1000)

    // Phase 3: Données rarement utilisées - après 3 secondes
    const timer3 = setTimeout(async () => {
      try {
        const prefetches: Promise<unknown>[] = []

        if (hasPermission('claims.view')) {
          prefetches.push(
            queryClient.prefetchQuery({
              queryKey: queryKeys.claims({ page: 1 }),
              queryFn: async () => {
                const { data } = await claimsApi.list({ page: 1 })
                return data.data
              },
              staleTime: staleTimes.standard,
            }),
          )
        }

        if (hasPermission('permissions.view')) {
          prefetches.push(
            queryClient.prefetchQuery({
              queryKey: queryKeys.permissionsGrouped,
              queryFn: async () => {
                const { data } = await permissionsApi.grouped()
                return data.data
              },
              staleTime: staleTimes.static,
            }),
          )
        }

        await Promise.allSettled(prefetches)
      } catch {
        // Silently fail
      }
    }, 3000)

    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
    }
  }, [queryClient])
}

/**
 * Fonction utilitaire pour précharger des données au hover
 * Utile pour les liens de navigation
 */
export function prefetchOnHover(
  queryClient: ReturnType<typeof useQueryClient>, 
  type: 'users' | 'rides' | 'drivers' | 'finance' | 'claims' | 'roles' | 'zones'
) {
  // Garde-fou : on ne précharge que si l'utilisateur peut réellement
  // consommer l'endpoint visé (sinon 403 inutile au survol).
  const { hasPermission } = useAuthStore.getState()
  const endpointPermissions: Record<string, string | string[]> = {
    users: 'users.view',
    rides: 'rides.view',
    // La page Chauffeurs liste via /admin/users?role=driver → users.view requis
    drivers: ['drivers.view', 'users.view'],
    finance: 'finance.view',
    claims: 'claims.view',
    roles: 'roles.view',
    zones: [],
  }
  const required = endpointPermissions[type] ?? []
  const requiredList = Array.isArray(required) ? required : [required]
  if (requiredList.length > 0 && !requiredList.every((p) => hasPermission(p))) return

  const prefetchMap: Record<string, () => Promise<void>> = {
    users: () => queryClient.prefetchQuery({
      queryKey: queryKeys.users({ page: 1 }),
      queryFn: async () => {
        const { data } = await usersApi.list({ page: 1 })
        return data.data
      },
      staleTime: staleTimes.standard,
    }),
    
    rides: () => queryClient.prefetchQuery({
      queryKey: queryKeys.rides({ page: 1 }),
      queryFn: async () => {
        const { data } = await ridesApi.list({ page: 1 })
        return data.data
      },
      staleTime: staleTimes.realtime,
    }),
    
    drivers: () => queryClient.prefetchQuery({
      queryKey: queryKeys.users({ role: 'driver', page: 1 }),
      queryFn: async () => {
        const { data } = await usersApi.list({ role: 'driver', page: 1 })
        return data.data
      },
      staleTime: staleTimes.standard,
    }),
    
    finance: () => queryClient.prefetchQuery({
      queryKey: queryKeys.financeOverview,
      queryFn: async () => {
        const { data } = await financeApi.overview()
        return mapFinanceOverview(data?.data ?? data)
      },
      staleTime: staleTimes.frequent,
    }),
    
    claims: () => queryClient.prefetchQuery({
      queryKey: queryKeys.claims({ page: 1 }),
      queryFn: async () => {
        const { data } = await claimsApi.list({ page: 1 })
        return data.data
      },
      staleTime: staleTimes.standard,
    }),
    
    roles: () => queryClient.prefetchQuery({
      queryKey: queryKeys.roles({}),
      queryFn: async () => {
        const { data } = await rolesApi.list()
        return data.data
      },
      staleTime: staleTimes.stable,
    }),
    
    zones: () => Promise.resolve(),
  }

  const prefetchFn = prefetchMap[type]
  if (prefetchFn) {
    prefetchFn().catch(() => {
      // Silently fail
    })
  }
}

/**
 * Hook qui retourne une fonction de prefetch pour les NavLinks
 */
export function usePrefetchOnHover() {
  const queryClient = useQueryClient()
  
  return useCallback((type: Parameters<typeof prefetchOnHover>[1]) => {
    prefetchOnHover(queryClient, type)
  }, [queryClient])
}
